import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { controlHeight, radius } from '@/shared/theme';
import { Icon } from './icon';

interface SearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** 38 (toolbar) or 40 (popover). */
  height?: number;
  tinted?: boolean;
  autoFocus?: boolean;
  sx?: SxProps<Theme>;
}

/** Compact search input with leading icon ("md-input"). */
export function SearchField({
  value,
  onChange,
  placeholder,
  height = controlHeight.md,
  tinted,
  autoFocus,
  sx,
}: SearchFieldProps) {
  return (
    <Box
      sx={[
        (theme) => ({
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          height,
          px: '12px',
          border: `1px solid ${theme.vars.palette.md.border}`,
          borderRadius: radius.control,
          background: tinted ? theme.vars.palette.md.panel2 : theme.vars.palette.md.panel,
          transition: 'box-shadow .14s',
          '&:focus-within': {
            borderColor: theme.vars.palette.md.primary,
            boxShadow: `0 0 0 4px ${theme.vars.palette.md.primary050}`,
          },
        }),
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <Box sx={{ display: 'flex', opacity: 0.5 }}>
        <Icon name="search" size={18} />
      </Box>
      <Box
        component="input"
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        autoFocus={autoFocus}
        sx={(theme) => ({
          flex: 1,
          minWidth: 0,
          border: 0,
          outline: 0,
          background: 'transparent',
          color: theme.vars.palette.md.text,
          font: 'inherit',
          fontSize: 13,
          '&::-webkit-search-cancel-button': { cursor: 'pointer' },
        })}
      />
    </Box>
  );
}
